/**
 * Steam account / sync sheet (INVENTORY §2 row 8; prototype lines 1215-1258).
 * Opened from the Playtests sync chip AND from Profile ▸ Steam Account.
 *
 * What is no longer theatre (INVENTORY §12):
 *   1. `SIGN IN THROUGH STEAM ↗` calls `steamLinkStart()`, which is the real
 *      OpenID hand-off in the Supabase repo and an immediate local link in dev.
 *      Either way this file calls the same action — "WE NEVER GET YOUR
 *      PASSWORD" is a product promise, not a UI string.
 *   2. `PRIVATE — GOT-IN DETECTION OFF` is reachable: it is
 *      `steamLink.profilePublic === false`, read from the sync result.
 *   3. `CHECK NOW` performs a real round-trip (`steamSyncNow`) and the footer's
 *      `CHECKED <NS AGO>` is the freshness slice's `lastOkAt`, so a failure
 *      leaves the timestamp where it was and toasts the retry.
 */

import { Avatar } from '../components/Avatar.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import {
  ptSyncVM,
  PT_SYNC_ATTR_LINK,
  PT_SYNC_NOTE,
  PT_SYNC_ATTR_TAIL,
  PT_SYNC_PRIVACY,
  PT_SYNC_STEAM_HREF,
  PT_SYNC_STEP2,
} from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

export default function PtSyncSheet(): JSX.Element {
  const s = useQd();
  const v = ptSyncVM(s, Date.now());

  return (
    <Sheet
      label="Steam sync sheet"
      maxHeight="90vh"
      hs
      onClose={s.closePtSync}
      header={{
        title: 'STEAM ACCOUNT',
        titleClr: 'var(--gt-text)',
        titleFs: '8.5px',
        note: PT_SYNC_NOTE,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, padding: '14px 14px 18px' }}>
        {/* step 1 */}
        <div
          style={{
            border: '1px solid ' + v.s1Bd,
            background: 'var(--gt-panel)',
            padding: 13,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <div style={{ fontFamily: PIXEL, fontSize: 9, color: v.s1Num, flex: 'none' }}>1</div>
            <div style={{ fontFamily: PIXEL, fontSize: '7.5px', color: 'var(--gt-text)', lineHeight: 1.6 }}>
              SIGN IN THROUGH STEAM
            </div>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.65, color: 'var(--gt-text-5)' }}>{v.s1Sub}</div>

          {v.linked ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Avatar style={{ flex: 'none' }} size={22} fs={7} ini={v.acctIni} bg={v.acctBg} hasAv={false} avBg="" />
              <span style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-text)' }}>{v.acctName}</span>
              <div style={{ flex: 1 }} />
              <span
                onClick={() => void s.steamUnlink()}
                style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-muted-2)', cursor: 'pointer', padding: '10px 4px' }}
              >
                UNLINK ✕
              </span>
            </div>
          ) : (
            <div
              onClick={() => {
                if (s.steamBusy) return;
                void s.steamLinkStart();
              }}
              style={{
                background: s.steamBusy ? 'var(--gt-border-2)' : 'var(--acc)',
                color: s.steamBusy ? 'var(--gt-muted)' : 'var(--gt-ink)',
                fontFamily: MONO,
                fontSize: '10.5px',
                fontWeight: 600,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: s.steamBusy ? 'progress' : 'pointer',
                boxSizing: 'border-box',
              }}
            >
              {s.steamBusy ? 'OPENING STEAM…' : 'SIGN IN THROUGH STEAM ↗'}
            </div>
          )}
        </div>

        {/* step 2 */}
        <div
          style={{
            border: '1px solid ' + v.s2Bd,
            background: 'var(--gt-panel)',
            padding: 13,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: PIXEL, fontSize: 9, color: v.s2Num, flex: 'none' }}>2</div>
            <div style={{ fontFamily: PIXEL, fontSize: '7.5px', color: 'var(--gt-text)', lineHeight: 1.6 }}>
              MAKE GAME DETAILS PUBLIC
            </div>
            <span
              style={{
                fontFamily: MONO,
                fontSize: 8,
                letterSpacing: '.05em',
                border: '1px solid ' + v.s2ChipBd,
                color: v.s2ChipClr,
                background: v.s2ChipBg,
                padding: '3px 6px',
              }}
            >
              {v.s2Chip}
            </span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.65, color: 'var(--gt-text-5)' }}>{PT_SYNC_STEP2}</div>
          <a
            href="https://steamcommunity.com/my/edit/settings"
            target="_blank"
            rel="noreferrer"
            style={{
              border: '1px solid var(--gt-border-3)',
              color: 'var(--gt-text-3)',
              fontFamily: MONO,
              fontSize: 10,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              boxSizing: 'border-box',
            }}
          >
            PRIVACY SETTINGS ↗
          </a>
        </div>

        {/* attribution + what we store — the app has no other legal surface */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
            fontFamily: MONO,
            fontSize: '8.5px',
            lineHeight: 1.7,
            color: 'var(--gt-muted-2)',
          }}
        >
          <div>
            <a
              href={PT_SYNC_STEAM_HREF}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--gt-text-3)', textDecoration: 'none' }}
            >
              {PT_SYNC_ATTR_LINK}
            </a>
            {PT_SYNC_ATTR_TAIL}
          </div>
          <div>{PT_SYNC_PRIVACY}</div>
        </div>

        {/* freshness footer + CHECK NOW */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            borderTop: '1px dashed var(--gt-border)',
            paddingTop: 12,
          }}
        >
          <div style={{ fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>{v.foot}</div>
          <div
            onClick={() => {
              if (!v.busy) void s.steamSyncNow();
            }}
            style={{
              border: '1px solid var(--gt-border-3)',
              color: 'var(--gt-text-3)',
              fontFamily: MONO,
              fontSize: 10,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: v.busy ? 'default' : 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {v.syncLbl}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
